import { Emitter } from "../../../base/common/events.js";
import { Disposable, IDisposable, toDisposable } from "../../../base/common/lifecycle.js";
import {
    type IPreviewFrameSession,
    type PreviewFrameSessionOptions,
    IPreviewProvider,
    IPreviewService,
    IPreviewSession,
    PreviewOptions,
    PreviewResult,
    PreviewSessionOptions,
} from "./preview.js";

/**
 * Engine-agnostic dispatcher: providers (one per engine, e.g. the three.js
 * ThreePreviewProvider) register themselves; requests go to the first provider
 * that {@link IPreviewProvider.canPreview}s the target.
 */
export class PreviewService extends Disposable implements IPreviewService {
    declare readonly _serviceBrand: undefined;

    private readonly _onDidChangeProviders = this._register(new Emitter<void>());
    readonly onDidChangeProviders = this._onDidChangeProviders.event;

    private readonly _providers: IPreviewProvider[] = [];

    registerProvider(provider: IPreviewProvider): IDisposable {
        this._providers.push(provider);
        this._onDidChangeProviders.fire();
        return toDisposable(() => {
            const index = this._providers.indexOf(provider);
            if (index >= 0) {
                this._providers.splice(index, 1);
                this._onDidChangeProviders.fire();
            }
        });
    }

    canPreview(target: object): boolean {
        return this.providerFor(target) !== null;
    }

    async getPreview(target: object, options?: PreviewOptions): Promise<PreviewResult | null> {
        const provider = this.providerFor(target);
        if (!provider) return null;
        return provider.renderPreview(target, options ?? {});
    }

    createFrameSession(target: object, options: PreviewFrameSessionOptions): IPreviewFrameSession | null {
        return this.providerFor(target)?.createFrameSession?.(target, options) ?? null;
    }

    createSession(target: object, options?: PreviewSessionOptions): IPreviewSession | null {
        const provider = this.providerFor(target);
        if (!provider?.createSession) return null;
        return provider.createSession(target, options ?? {});
    }

    invalidate(target?: object): void {
        for (const provider of this._providers) {
            provider.invalidate(target);
        }
    }

    private providerFor(target: object): IPreviewProvider | null {
        if (!target || typeof target !== "object") return null;
        for (const provider of this._providers) {
            try {
                if (provider.canPreview(target)) return provider;
            }
            catch {
                // a broken provider must not take down the others
            }
        }
        return null;
    }
}
